import AttachFileIcon from '@mui/icons-material/AttachFile'
import DownloadIcon from '@mui/icons-material/Download'
import InfoOutlineIcon from '@mui/icons-material/InfoOutline'
import { Box, Button, Drawer, Stack, Typography } from '@mui/material'
import { ProductMasterBatchJobOperationTypeEnum } from '@ulysses-inc/harami_api_client'
import { FC, useCallback } from 'react'
import { Header } from 'src/components/header/Header'
import { HeaderTitle } from 'src/components/header/HeaderTitle'
import { useSnackbar } from 'src/components/snackbar/useSnackbar'
import { useModalClosePreventionIsPending } from 'src/hooks/useModalClosePrevention'
import {
  attachFileIconSx,
  containerSx,
  csvUploadSectionSx,
  csvUploadTitleSx,
  footerSx,
  iconBoxSx,
  infoIconSx,
  infoTextSx,
  instructionContainerSx,
  instructionStepsContainerSx,
  selectedFileSx,
  stepButtonContainerSx,
  stepContainerSx,
  stepNoteSx,
  stepTitleSx,
} from '../../../components/RecordsAddUpdateDrawer/RecordsAddUpdateDrawer.styles'
import { useFileSelection } from '../../../hooks/useFileSelection'
import { useDownloadMasterCsv } from '../../hooks/useDownloadMasterCsv'
import { useMasterFileUpload } from '../../hooks/useMasterFileUpload'

type Props = {
  masterUuid: string
  masterName: string
  open: boolean
  closeDrawer: () => void
}

const MasterRecordsAddUpdateDrawer: FC<Props> = ({
  masterUuid,
  masterName,
  open,
  closeDrawer,
}) => {
  const { showErrorSnackbar } = useSnackbar()

  const {
    fileInputRef,
    selectedFile,
    handleFileSelect,
    handleFileChange,
    resetFile,
  } = useFileSelection()

  const { uploadFile, isProcessing } = useMasterFileUpload({
    masterUuid,
    onClose: closeDrawer,
    operationType: ProductMasterBatchJobOperationTypeEnum.Upsert,
  })

  const { downloadMasterCsv, isDownloading } = useDownloadMasterCsv(
    masterUuid,
    masterName,
  )

  const handleClose = useCallback(() => {
    resetFile()
    closeDrawer()
  }, [resetFile, closeDrawer])

  const handleDrawerClose = useModalClosePreventionIsPending(
    handleClose,
    isProcessing || isDownloading,
  )

  const onUpload = useCallback(() => {
    if (!selectedFile) {
      showErrorSnackbar('CSVファイルを選択してください')
      return
    }
    uploadFile(selectedFile)
    resetFile()
  }, [selectedFile, uploadFile, resetFile, showErrorSnackbar])

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleDrawerClose}
      sx={{ '& .MuiDrawer-paper': { width: '600px' } }}
    >
      <Header>
        <HeaderTitle title="CSVで追加・更新" />
      </Header>

      <Box sx={containerSx}>
        <Stack sx={{ flex: 1 }}>
          <Stack gap={2} sx={{ padding: 3 }}>
            <Box sx={instructionContainerSx}>
              <Box sx={iconBoxSx}>
                <InfoOutlineIcon sx={infoIconSx} />
                <Typography sx={infoTextSx}>追加・更新の手順</Typography>
              </Box>
              <Box sx={instructionStepsContainerSx}>
                <Box sx={stepContainerSx}>
                  <Typography sx={stepTitleSx}>
                    1. 登録済みマスタデータをダウンロードします。
                  </Typography>
                  <Box sx={stepButtonContainerSx}>
                    <Button
                      size="medium"
                      variant="outlined"
                      color="primary"
                      startIcon={<DownloadIcon />}
                      onClick={downloadMasterCsv}
                      disabled={isDownloading}
                    >
                      {isDownloading
                        ? 'ダウンロード中...'
                        : '登録済みマスタデータ'}
                    </Button>
                  </Box>
                </Box>
                <Box sx={stepContainerSx}>
                  <Typography sx={stepTitleSx}>
                    2.
                    ダウンロードしたCSVファイルを開き、追加・更新したい項目の情報を入力してください
                  </Typography>
                  <Typography sx={stepNoteSx}>
                    ※編集対象でない項目であっても最新データを記入してください。空欄にすると上書きされます。
                  </Typography>
                </Box>
                <Typography sx={stepTitleSx}>
                  3. CSVファイルを保存し、下記よりアップロードしてください
                </Typography>
              </Box>
            </Box>

            <Box sx={csvUploadSectionSx}>
              <Typography sx={csvUploadTitleSx}>CSVアップロード</Typography>
              <Box>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".csv,text/csv"
                  style={{ display: 'none' }}
                />
                <Button
                  size="medium"
                  variant="contained"
                  color="primary"
                  onClick={handleFileSelect}
                >
                  CSVファイルを選択
                </Button>
              </Box>
              {selectedFile && (
                <Box sx={iconBoxSx}>
                  <Button
                    size="medium"
                    startIcon={<AttachFileIcon sx={attachFileIconSx} />}
                    onClick={handleFileSelect}
                  >
                    <Typography sx={selectedFileSx}>
                      {selectedFile.name}
                    </Typography>
                  </Button>
                </Box>
              )}
            </Box>
          </Stack>
        </Stack>

        <Box sx={footerSx}>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleClose}
            disabled={isProcessing}
          >
            閉じる
          </Button>
          <Button
            type="button"
            variant="contained"
            onClick={onUpload}
            disabled={isProcessing || !selectedFile}
          >
            {isProcessing ? 'アップロード中...' : 'アップロードする'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  )
}

export default MasterRecordsAddUpdateDrawer
